import React, { useState } from 'react';
import { Mail, AlertCircle, Shield } from 'lucide-react';
import { useHideNavOnModal } from '../hooks/useHideNavOnModal';

const ForceEmailSetupModal = ({ isOpen, onSuccess, api }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Esconde nav quando modal está aberto
  useHideNavOnModal(isOpen);

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      setError('Digite um email válido');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Setup email and send verification code
      const response = await api.post('/api/profile/email/setup', { email });
      
      // Pass email_masked to parent
      onSuccess({ email_masked: response.data.email_masked });
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao salvar email');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-[9999] backdrop-blur-md modal-anim">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden border-2 border-red-700">
        {/* Header - No close button */}
        <div className="relative bg-gradient-to-br from-red-600 via-orange-600 to-red-600 px-8 py-6">
          <div className="absolute inset-0 bg-gradient-to-br from-red-400/20 to-orange-400/20"></div>
          <div className="relative text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Shield size={32} className="text-white" strokeWidth={2.5} />
            </div>
            <h2 className="text-2xl font-black text-white mb-1">
              ⚠️ Configuração Obrigatória
            </h2>
            <p className="text-red-100 text-sm font-medium">É necessário configurar um email para continuar</p>
          </div>
        </div>

        <div className="p-8 space-y-6">
          {/* Alert */}
          <div className="bg-red-900/30 border-2 border-red-700 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-red-400 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-bold text-white text-lg mb-2">Por que preciso de um email?</h3>
                <ul className="text-sm text-red-200 space-y-2">
                  <li>• Segurança: Verificação em cada login</li>
                  <li>• Recuperação: Caso perca acesso à conta</li>
                  <li>• Notificações: Alertas importantes do sistema</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Email Input */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-3">
                <div className="flex items-center gap-2">
                  <Mail size={18} />
                  Digite seu melhor email
                </div>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                placeholder="seu-email@exemplo.com"
                className="w-full p-4 bg-gray-800 text-white border-2 border-gray-600 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-lg"
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-2">
                Um código de verificação será enviado para este email em cada login
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm font-semibold bg-red-900/30 px-4 py-3 rounded-lg border border-red-700">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading || !email}
            className="w-full px-6 py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-xl hover:from-red-700 hover:to-orange-700 transition-all font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-lg"
          >
            {loading ? 'Salvando...' : 'Salvar e Continuar'}
          </button>

          {/* Warning */}
          <div className="bg-yellow-900/30 border border-yellow-700 rounded-xl p-4">
            <p className="text-xs text-yellow-200 text-center font-semibold">
              ⚠️ O sistema permanecerá bloqueado até você configurar um email válido
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForceEmailSetupModal;
